import { createSelector } from "reselect";
import {
  CoinName,
  DisplayCurrency,
  FixMeLater,
  PrecisionsData,
  SymbolsMetadata,
  WorkspaceType,
} from "@shared/types";
import { DISPLAY_CURRENCIES } from "@shared/common/constants";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { getStoreFromSliceName } from "@shared/utils/slices";
import { ValueOf } from "@shared/types/utils";
import { sharedLib } from "@shared/services/shared.service";

export const COMMON_SLICE_PATH = "common";

export interface CommonState {
  displayCurrency: ValueOf<typeof DISPLAY_CURRENCIES>;
  availableIconAssets: string[];
  isDarkMode: boolean;
  isAuth: boolean;
  unavailableIconLinks: string[];
  workspace?: WorkspaceType;
  workspaceInitialized?: boolean;
  precisionsData?: PrecisionsData;
  symbolsMetadata?: SymbolsMetadata;
  shouldFetchPortfolioData?: boolean;
}

export const createCommonSlice = (isDarkMode?: boolean) => {
  const InitialState: CommonState = {
    displayCurrency: DISPLAY_CURRENCIES.USD,
    availableIconAssets: [],
    isDarkMode: !!isDarkMode,
    isAuth: false,
    unavailableIconLinks: [],
    workspace: WorkspaceType.USER,
    workspaceInitialized: false,
    precisionsData: undefined,
    symbolsMetadata: undefined,
    shouldFetchPortfolioData: undefined,
  };

  return createSlice({
    name: COMMON_SLICE_PATH,
    initialState: InitialState,
    reducers: {
      setDisplayCurrency(state, action) {
        state.displayCurrency = action.payload;
      },
      removeFromAvailableIconAssets(state, action) {
        state.availableIconAssets = state.availableIconAssets.filter(
          (asset) => asset !== action.payload
        );
      },
      setIsDarkMode(state, action) {
        state.isDarkMode = action.payload;
        sharedLib.storageService.setIsDarkMode(action.payload);
      },
      setIsAuth(state, action) {
        state.isAuth = action.payload;
      },
      addToUnavailableIconLinks(state, action) {
        state.unavailableIconLinks.push(action.payload);
      },
      setWorkspace(state, action: PayloadAction<WorkspaceType>) {
        state.workspace = action.payload;
      },
      setWorkspaceInitialized(state, action) {
        state.workspaceInitialized = action.payload;
      },
      setShouldFetchPortfolioData(state, action) {
        state.shouldFetchPortfolioData = action.payload;
      },
    },
    extraReducers: (builder) => {
      builder.addMatcher(
        sharedLib.sharedApi.common.endpoints.getAvailableIconAssets
          .matchFulfilled,
        (state: CommonState, action: PayloadAction<CoinName[]>) => {
          state.availableIconAssets = (action.payload || []).map((coin) =>
            coin.toLowerCase()
          );
        }
      );
      builder.addMatcher(
        sharedLib.sharedApi.common.endpoints.getPrecisions.matchFulfilled,
        (state: CommonState, action: PayloadAction<PrecisionsData>) => {
          state.precisionsData = action.payload;
        }
      );
      builder.addMatcher(
        sharedLib.sharedApi.common.endpoints.getSymbolsMetadata.matchFulfilled,
        (state: CommonState, action: PayloadAction<SymbolsMetadata>) => {
          state.symbolsMetadata = action.payload;
        }
      );
    },
  });
};

export const selectCommonState = (state: FixMeLater): CommonState =>
  getStoreFromSliceName(state, COMMON_SLICE_PATH);

export const selectDisplayCurrency = createSelector(
  selectCommonState,
  (headerStore) =>
    (headerStore.displayCurrency || DISPLAY_CURRENCIES.USD) as DisplayCurrency
);

export const selectAvailableIconAssets = createSelector(
  selectCommonState,
  (headerStore) => new Set(headerStore.availableIconAssets)
);

export const selectIsDarkMode = createSelector(
  selectCommonState,
  (headerStore) => headerStore.isDarkMode
);

export const selectIsAuth = createSelector(
  selectCommonState,
  (headerStore) => headerStore.isAuth
);

export const selectUnavailableIconLinks = createSelector(
  selectCommonState,
  (headerStore) => new Set(headerStore.unavailableIconLinks)
);

export const selectWorkspace = createSelector(
  selectCommonState,
  (store) => store.workspace
);

export const selectWorkspaceInitialized = createSelector(
  selectCommonState,
  (store) => store.workspaceInitialized
);

export const selectPrecisionsData = createSelector(
  selectCommonState,
  (store) => store.precisionsData
);

export const selectSymbolsMetadata = createSelector(
  selectCommonState,
  (store) => store.symbolsMetadata
);

export const selectShouldFetchPortfolioData = createSelector(
  selectCommonState,
  (store) => store.shouldFetchPortfolioData
);
